import { useEffect, useMemo } from 'react';
import { useAuth } from 'react-oidc-context';
import { AnnouncementsService } from '../services/AnnouncementsService';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import {
    setAnnouncementsLoadStatus,
    setEligibleAnnouncements,
} from '../../redux/features/announcements/announcementsSlice';
import { RootState } from '../../redux/app/store';

/**
 * Loads the user's eligible "what's new" announcements once auth + feature flags are
 * ready (#1618). Independent of WelcomeDialog's localStorage — backend-driven so the
 * same user does not see the same announcement when switching companies.
 */
export const useInitialAnnouncements = () => {
    const dispatch = useAppDispatch();
    const auth = useAuth();
    const token = auth.user?.access_token;
    const featureFlags = useAppSelector((state: RootState) => state.app.serviceInfo.featureFlags);
    const loadStatus = useAppSelector((state: RootState) => state.announcements.loadStatus);

    // Returning undefined when the token is missing keeps the invariant "service exists ⇒
    // we have a real access token" in the type system rather than relying on the
    // isAuthenticated gate below. A future caller that forgets the gate gets a noop, not
    // a silently-unauthenticated request with an empty Authorization header.
    const service = useMemo(() => (token ? new AnnouncementsService(token) : undefined), [token]);

    useEffect(() => {
        if (!auth.isAuthenticated || !service) {
            return;
        }
        if (!featureFlags.AnnouncementsEnabled) {
            return;
        }
        if (loadStatus !== 'idle') {
            return;
        }

        const fetchEligible = async () => {
            dispatch(setAnnouncementsLoadStatus('loading'));
            try {
                const ids = await service.getEligibleAsync();
                dispatch(setEligibleAnnouncements(ids));
            } catch (err) {
                console.warn('useInitialAnnouncements: failed to load eligible announcements', err);
                // Mark as loaded (not idle) so the effect doesn't immediately re-fire and
                // hammer the endpoint. A page reload resets the slice and retries.
                dispatch(setAnnouncementsLoadStatus('loaded'));
            }
        };

        void fetchEligible();
    }, [auth.isAuthenticated, featureFlags.AnnouncementsEnabled, loadStatus, dispatch, service]);
};
